import { useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Info, Pencil, Trash2 } from 'lucide-react';

import { getVocabularyTerm } from '@/api/vocabularies';
import { InlineTermInfoPanel } from '@/components/vocabulary/InlineTermInfoPanel';
import { useVocabularyInfo } from '@/components/vocabulary/useVocabularyInfo';
import { useFeatureMutations } from '@/features/patient-record/hooks/useFeatureMutations';
import { inputFromFeature } from '@/features/patient-record/lib/featureForm';
import { MODIFIER_SPECS } from '@/features/patient-record/lib/featureModifiers';
import { cn } from '@/lib/utils';
import type { FeatureStatus, FeatureViewModel } from '@/features/patient-record/types';

import { RowActionsMenu } from '@/components/ui/row-actions-menu';

import { FeatureRowEditor } from './FeatureRowEditor';
import { FeatureStatusToggle } from './FeatureStatusToggle';

/** Patient phenotypes are HPO terms; the info panel fetches details from this vocabulary. */
const PHENOTYPE_VOCABULARY_ID = 'hpo';

interface FeatureRowProps {
  readonly guid: string;
  readonly feature: FeatureViewModel;
  readonly expanded: boolean;
  readonly onToggleExpand: () => void;
  readonly onDelete: () => void;
}

export function FeatureRow({
  guid,
  feature,
  expanded,
  onToggleExpand,
  onDelete,
}: FeatureRowProps): ReactElement {
  const { t } = useTranslation();
  const { update } = useFeatureMutations(guid);

  // Only catalogue terms (those with an HPO id) have a detail panel — custom free-text
  // phenotypes have nothing to fetch, so they show no info button.
  const hasTerm = feature.hpoId.length > 0;
  const [infoOpen, setInfoOpen] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);
  const infoPanelRef = useRef<HTMLDivElement | null>(null);

  const vocabInfo = useVocabularyInfo(PHENOTYPE_VOCABULARY_ID);
  const termQuery = useQuery({
    queryKey: ['vocabulary', PHENOTYPE_VOCABULARY_ID, 'term', feature.hpoId],
    queryFn: ({ signal }) => getVocabularyTerm(PHENOTYPE_VOCABULARY_ID, feature.hpoId, signal),
    enabled: infoOpen && hasTerm,
    staleTime: 5 * 60_000,
  });
  const term = termQuery.data ?? null;

  // Close the floating panel on Escape or a click anywhere outside it. The panel is portalled
  // to <body>, so it lives outside this row's subtree — exempt it explicitly. The info button's
  // own mousedown stops propagation, so toggling it back off never trips this handler.
  useEffect(() => {
    if (!infoOpen) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (infoPanelRef.current?.contains(event.target as Node) === true) return;
      setInfoOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setInfoOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [infoOpen]);

  const presentModifiers = MODIFIER_SPECS.filter(
    (spec) => feature.modifiers[spec.key] !== undefined,
  );

  const setStatus = (next: FeatureStatus): void => {
    if (next === feature.status) return;
    update.mutate({
      id: feature.id,
      input: { ...inputFromFeature(feature), status: next },
    });
  };

  return (
    <div className="group rounded-md -mx-2 px-2 py-1 hover:bg-accent/40 transition-colors">
      <div ref={headerRef} className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0 flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              {/* The label opens the edit modal. The info button is a sibling —
                  never nested inside it — so the two clicks never fight. */}
              <button
                type="button"
                onClick={onToggleExpand}
                aria-haspopup="dialog"
                className="min-w-0 truncate text-left text-sm font-medium text-card-foreground hover:text-primary transition-colors"
              >
                {feature.label}
              </button>
              {hasTerm && (
                <span className="text-[10px] text-muted-foreground bg-muted/60 px-1.5 py-0.5 rounded border border-border/60 font-mono">
                  {feature.hpoId}
                </span>
              )}
              {hasTerm && (
                <button
                  type="button"
                  aria-label={t('vocab.picker.info', { term: feature.label })}
                  aria-expanded={infoOpen}
                  onMouseDown={(event) => {
                    // Keep this off the document mousedown handler that closes the panel.
                    event.stopPropagation();
                  }}
                  onClick={() => {
                    setInfoOpen((prev) => !prev);
                  }}
                  className={cn(
                    'shrink-0 inline-flex items-center text-muted-foreground hover:text-foreground transition-colors',
                    infoOpen && 'text-foreground',
                  )}
                >
                  <Info className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              )}
            </div>
            {(feature.onset !== null ||
              feature.severity !== null ||
              feature.occurrence !== null ||
              presentModifiers.length > 0) && (
                <dl className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-muted-foreground">
                  {feature.onset !== null && (
                    <div className="flex gap-1">
                      <dt className="font-medium">{t('patient.phenotypes.fields.onset')}</dt>
                      <dd>{feature.onset}</dd>
                    </div>
                  )}
                  {feature.severity !== null && (
                    <div className="flex gap-1">
                      <dt className="font-medium">{t('patient.phenotypes.fields.severity')}</dt>
                      <dd>{feature.severity}</dd>
                    </div>
                  )}
                  {presentModifiers.map((spec) => (
                    <div key={spec.key} className="flex gap-1">
                      <dt className="font-medium">
                        {t(`patient.phenotypes.fields.${spec.key}`)}
                      </dt>
                      <dd>{feature.modifiers[spec.key]?.label}</dd>
                    </div>
                  ))}
                  {feature.occurrence !== null && (
                    <div className="flex gap-1">
                      <dt className="font-medium">
                        {t('patient.phenotypes.fields.occurrence')}
                      </dt>
                      <dd>
                        {t(
                          `patient.phenotypes.occurrence.${
                            feature.occurrence === 'PRE_APPOINTMENT'
                              ? 'preAppointment'
                              : feature.occurrence === 'POST_APPOINTMENT'
                                ? 'postAppointment'
                                : 'both'
                          }`,
                        )}
                      </dd>
                    </div>
                  )}
                </dl>
              )}
            {feature.notes !== null && (
              <p className="text-xs text-muted-foreground mt-1 italic line-clamp-2">
                {feature.notes}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <FeatureStatusToggle
            value={feature.status}
            onChange={setStatus}
            disabled={update.isPending}
          />
          <RowActionsMenu
            label={t('patient.phenotypes.actionsLabel', { defaultValue: 'Phenotype actions' })}
            actions={[
              {
                label: t('patient.phenotypes.editDetails', { defaultValue: 'Edit details' }),
                icon: <Pencil className="h-3.5 w-3.5" aria-hidden="true" />,
                onSelect: onToggleExpand,
              },
              {
                label: t('patient.phenotypes.delete'),
                icon: <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />,
                onSelect: onDelete,
                destructive: true,
                separatorBefore: true,
              },
            ]}
          />
        </div>
      </div>
      {infoOpen && term !== null && (
        <InlineTermInfoPanel
          term={term}
          display={vocabInfo?.display ?? null}
          vocabularyName={vocabInfo?.name ?? null}
          version={vocabInfo?.dataVersion ?? null}
          widthAnchorRef={headerRef}
          panelRef={infoPanelRef}
        />
      )}
      {expanded && (
        <FeatureRowEditor
          guid={guid}
          feature={feature}
          onClose={onToggleExpand}
        />
      )}
    </div>
  );
}
